import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ArrowLeft, BadgeCheck, Loader2, MessageSquareText } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../../context/AuthContext";
import { useDraftReviews } from "../../hooks/reviewQueries";
import { deleteReview } from "../../services/reviewService";
import ReportModal from "../Modal/Reportmodal"; 
import FetchErrorState from "../Common/FetchErrorState";
import ErrorModal from "../Modal/ErrorModal";

const RATING_LABELS = ["", "Poor", "Fair", "Good", "Great", "Excellent"];

const RECOMMENDATION_LABELS = {
  yes: "🟢 Yes, it's ready",
  needs_improvement: "🟡 Needs a few improvements",
  not_yet: "🔴 Not yet",
};

const useIntersectionObserver = (callback, options = {}) => {
  const targetRef = useRef(null);

  useEffect(() => {
    const target = targetRef.current;
    if (!target) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) callback();
      },
      { threshold: 0.1, rootMargin: "100px", ...options }
    );

    observer.observe(target);
    return () => {
      if (target) observer.unobserve(target);
    };
  }, [callback, options]);

  return targetRef;
};

const getInitials = (name) => {
  if (!name) return "";
  const words = name.trim().split(/\s+/);
  if (words.length === 1) return words[0].charAt(0).toUpperCase();
  return (words[0].charAt(0) + words[1].charAt(0)).toUpperCase();
};

const getProfilePicture = (profilePic) => {
  if (profilePic && typeof profilePic === "object") {
    return profilePic.small || profilePic.medium;
  }
  return null;
};

const ReviewItem = React.memo(
  ({ review, isSelected, onToggleSelect, onDelete, onReport, isDeleting }) => {
    const navigate = useNavigate();
    const reviewer = review.reviewer || {};
    const profilePictureUrl = getProfilePicture(reviewer.profilePicture);

    const handleProfileClick = (e) => {
      e.stopPropagation();
      if (reviewer.username) {
        navigate(`/${reviewer.username}`);
      }
    };

    return (
      <div className="py-2 border-b border-gray-100 last:border-b-0">
        <div
          className="flex items-center gap-3 mb-2 cursor-pointer w-fit"
          onClick={handleProfileClick}
        >
          {profilePictureUrl ? (
            <img
              src={profilePictureUrl}
              alt={reviewer.name}
              className="w-9 h-9 rounded-full object-cover border-[0.5px] border-gray-200 hover:opacity-80 transition-opacity"
            />
          ) : (
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center overflow-hidden hover:opacity-80 transition-opacity">
              <span className="text-slate-600 font-semibold text-xs">
                {getInitials(reviewer.name)}
              </span>
            </div>
          )}

          <div className="flex items-center gap-1">
            <span className="text-sm font-semibold text-black hover:text-gray-500 transition-colors">
              {reviewer.username}
            </span>
            {reviewer.isVerified && (
              <BadgeCheck className="w-4 h-4 fill-[#b0d284] stroke-white flex-shrink-0" />
            )}
          </div>
        </div>

        <div
          onClick={() => onToggleSelect(review._id)}
          className="cursor-pointer"
        >
          <div className="flex items-center gap-2 mb-1.5">
            <span className="text-sm">
              {"⭐".repeat(review.rating)}
              {"☆".repeat(5 - review.rating)}
            </span>
            <span className="text-xs text-gray-500">
              {RATING_LABELS[review.rating]}
            </span>
          </div>

          <p className="text-xs font-medium text-black mb-1.5">
            {RECOMMENDATION_LABELS[review.recommendation]}
          </p>

          <p className="text-sm text-gray-700 leading-relaxed">
            {review.thoughts}
          </p>
        </div>

        <div
            className={`overflow-hidden transition-all duration-300 ease-out ${
                isSelected ? "max-h-16 opacity-100 mt-2" : "max-h-0 opacity-0 mt-0"
            }`}
            >
            <div className="flex gap-2">
                <button
                    onClick={(e) => {
                    e.stopPropagation();
                    onDelete(review._id);
                    }}
                    disabled={isDeleting}
                    className="flex-1 px-4 py-1 bg-red-600 text-white text-sm font-semibold rounded-md text-center hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                    {isDeleting ? "Deleting..." : "Delete"}
                </button>

                <button
                    onClick={(e) => {
                    e.stopPropagation();
                    onReport();
                    }}
                    className="flex-1 px-4 py-1 bg-red-50 border border-red-300 text-red-600 text-sm font-semibold rounded-md text-center hover:bg-red-100 transition-colors"
                >
                    Report
                </button>
            </div>
        </div>
      </div>
    );
  }
);

const DraftReviewsModal = ({ isOpen, onClose, draftId }) => {
  const { apiCall } = useAuth();
  const queryClient = useQueryClient();

  const [selectedReviewId, setSelectedReviewId] = useState(null);
  const [deletingReviewId, setDeletingReviewId] = useState(null);
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [errorModal, setErrorModal] = useState({ isOpen: false, message: "" });
  const [reportSuccessMsg, setReportSuccessMsg] = useState(null); 

  const {
    data: reviewsData,
    isLoading,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
  } = useDraftReviews(draftId);

  const allReviews = useMemo(() => {
    if (!reviewsData) return [];
    return reviewsData.pages.flatMap((page) => page.reviews || []);
  }, [reviewsData]);

  const handleLoadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage && !isLoading) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, isLoading, fetchNextPage]);

  const loadMoreRef = useIntersectionObserver(handleLoadMore);

  const handleToggleSelect = useCallback((reviewId) => {
    setSelectedReviewId((prev) => (prev === reviewId ? null : reviewId));
  }, []);

  const handleDelete = useCallback(
    async (reviewId) => {
      setDeletingReviewId(reviewId);

      const result = await deleteReview(apiCall, reviewId);

      setDeletingReviewId(null);

      if (!result.success) {
        setErrorModal({
          isOpen: true,
          message: result.error || "Failed to delete review. Please try again.",
        });
        return;
      }

      setSelectedReviewId(null);
      queryClient.invalidateQueries({ queryKey: ["draftReviews", draftId] });
    },
    [apiCall, queryClient, draftId]
  );

  const handleReport = useCallback(() => {
    // selectedReviewId already holds the id — no separate state needed for it
    setIsReportModalOpen(true);
  }, []);

  const handleCloseReport = useCallback(() => {
    setIsReportModalOpen(false);
    setSelectedReviewId(null);
  }, []);

  const handleReportSuccess = useCallback((message) => {
    setIsReportModalOpen(false);
    setSelectedReviewId(null);
    setReportSuccessMsg(message);
    setTimeout(() => setReportSuccessMsg(null), 3000);
  }, []);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-[60]"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white w-full h-full sm:h-auto sm:w-full sm:max-w-md sm:rounded-2xl shadow-lg sm:max-h-[85vh] flex flex-col"
      >
        {/* Modal Header */}
        <div className="px-4 sm:px-6 py-4 border-b border-gray-100 flex items-center gap-3 flex-shrink-0">
          <button
            onClick={onClose}
            className="sm:hidden p-1 -ml-1 text-black"
            aria-label="Go back"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h3 className="text-lg font-semibold text-black">Reviews</h3>
        </div>

        {/* Modal Content */}
        <div className="px-4 sm:px-6 py-2 overflow-y-auto flex-1">
          {isLoading && allReviews.length === 0 ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
            </div>
          ) : error && allReviews.length === 0 ? (
            <FetchErrorState
              message="We couldn't load the reviews for this draft."
              onRetry={() => refetch()}
            />
          ) : allReviews.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <p className="text-sm font-medium text-gray-900 mb-1">No reviews yet</p>
              <p className="text-xs text-gray-500 max-w-xs">
                Reviews for this draft will show up here
              </p>
            </div>
          ) : (
            <>
              {allReviews.map((review) => (
                <ReviewItem
                  key={review._id}
                  review={review}
                  isSelected={selectedReviewId === review._id}
                  onToggleSelect={handleToggleSelect}
                  onDelete={handleDelete}
                  onReport={handleReport}
                  isDeleting={deletingReviewId === review._id}
                />
              ))}

              {hasNextPage && (
                <div ref={loadMoreRef} className="flex items-center justify-center py-6">
                  <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
                </div>
              )}
            </>
          )}
        </div>
      </div>
      {isReportModalOpen && selectedReviewId && (
        <ReportModal
          targetId={selectedReviewId}
          targetType="review"
          onClose={handleCloseReport}
          onSuccess={handleReportSuccess}
        />
      )}

      {reportSuccessMsg && (
        <div className="fixed bottom-6 inset-x-4 z-[70] flex justify-center">
          <div className="w-full max-w-md bg-black text-white text-sm font-medium px-4 py-3 rounded-full shadow-lg text-center">
            {reportSuccessMsg}
          </div>
        </div>
      )}

      <ErrorModal
        isOpen={errorModal.isOpen}
        message={errorModal.message}
        onClose={() => setErrorModal({ isOpen: false, message: "" })}
      />
    </div>
  );
};

export default DraftReviewsModal;